import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export interface WaterTopologyOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface WaterTopologyOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

export type WaterCircuitKind = "shared_sump" | "shared_loop" | "other";
export type TankWaterMode = "connected" | "independent";

function create(
  context: WaterTopologyOperationContext,
  operationType: string,
  entityId: string,
  payload: Readonly<Record<string, unknown>>,
  expectedVersion: number,
  options?: WaterTopologyOperationOptions,
): OfflineOperation {
  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    !entityId
  ) {
    throw new Error("WATER_TOPOLOGY_CONTEXT_REQUIRED");
  }

  if (!Number.isInteger(expectedVersion) || expectedVersion < 0) {
    throw new Error("WATER_TOPOLOGY_VERSION_INVALID");
  }

  return createOfflineOperation(
    {
      operationType,
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId,
      performedAt: context.performedAt,
      baseVersion: String(expectedVersion),
      payload,
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

export function buildWaterCircuitUpsertOperation(
  context: WaterTopologyOperationContext,
  input: {
    circuitId: string;
    circuitCode: string;
    displayName: string;
    circuitKind: WaterCircuitKind;
    nominalVolumeL?: number;
    isActive: boolean;
    expectedVersion: number;
  },
  options?: WaterTopologyOperationOptions,
): OfflineOperation {
  const circuitCode = input.circuitCode.trim().toUpperCase();
  const displayName = input.displayName.trim();

  if (!input.circuitId || circuitCode.length < 1 || circuitCode.length > 40) {
    throw new Error("WATER_CIRCUIT_CODE_INVALID");
  }

  if (displayName.length < 1 || displayName.length > 120) {
    throw new Error("WATER_CIRCUIT_NAME_INVALID");
  }

  if (!["shared_sump", "shared_loop", "other"].includes(input.circuitKind)) {
    throw new Error("WATER_CIRCUIT_KIND_INVALID");
  }

  if (
    input.nominalVolumeL !== undefined &&
    (!Number.isFinite(input.nominalVolumeL) || input.nominalVolumeL <= 0)
  ) {
    throw new Error("WATER_CIRCUIT_VOLUME_INVALID");
  }

  if (!Number.isInteger(input.expectedVersion) || input.expectedVersion < 0) {
    throw new Error("WATER_CIRCUIT_VERSION_INVALID");
  }

  return create(
    context,
    "water.circuit.upsert",
    input.circuitId,
    {
      circuitId: input.circuitId,
      circuitCode,
      displayName,
      circuitKind: input.circuitKind,
      ...(input.nominalVolumeL === undefined ? {} : { nominalVolumeL: input.nominalVolumeL }),
      isActive: input.isActive,
      expectedVersion: input.expectedVersion,
    },
    input.expectedVersion,
    options,
  );
}

export function buildTankWaterProfileSetOperation(
  context: WaterTopologyOperationContext,
  input: {
    tankId: string;
    expectedVersion: number;
    mode: TankWaterMode;
    circuitId?: string;
    independentReason?: string;
  },
  options?: WaterTopologyOperationOptions,
): OfflineOperation {
  if (!input.tankId || !Number.isInteger(input.expectedVersion) || input.expectedVersion < 0) {
    throw new Error("WATER_PROFILE_INPUT_INVALID");
  }

  const independentReason = input.independentReason?.trim();

  if (input.mode === "connected") {
    if (!input.circuitId) throw new Error("WATER_PROFILE_CIRCUIT_REQUIRED");

    return create(
      context,
      "water.profile.set",
      input.tankId,
      {
        tankId: input.tankId,
        expectedVersion: input.expectedVersion,
        mode: "connected",
        circuitId: input.circuitId,
      },
      input.expectedVersion,
      options,
    );
  }

  if (input.circuitId) {
    throw new Error("WATER_PROFILE_INDEPENDENT_CIRCUIT_FORBIDDEN");
  }

  return create(
    context,
    "water.profile.set",
    input.tankId,
    {
      tankId: input.tankId,
      expectedVersion: input.expectedVersion,
      mode: "independent",
      ...(independentReason ? { independentReason } : {}),
    },
    input.expectedVersion,
    options,
  );
}
